import { GalleryTypes } from '@customTypes/gallery';
import { getUrlParamNumberValue, updateParamInUrl } from '@utils/urlParam';
import { initializeGlide } from './gallerySlideOrScroll';
import { scrollToTop } from '@utils/scroll';
import { GTMTrack } from '@utils/dataLayer';
import { handleToggleViewTransitions } from './galleryGridTransitions';
import { handleZoom } from './galleryZoom';
import { handleCarouselArrows, handleKeyboardArrows } from './galleryGTMTracking';

export class GalleryState implements GalleryTypes.State {
  activeItem = 0;

  itemInUrl: number;

  lockedFrom: number | boolean;

  rootElement: HTMLElement;

  isDesktop: boolean;

  view: GalleryTypes.View;

  previousBreakPoint: GalleryTypes.Breakpoints;

  private static instance: GalleryState;

  private constructor(
    rootElement: HTMLElement = document.querySelector('[data-gallery-init]') as HTMLElement,
    activeItem = 0,
    lockedFrom = false,
    isDesktop = false,
    view: GalleryTypes.View = 'slideOrScroll',
    previousBreakPoint: GalleryTypes.Breakpoints = 'desktop'
  ) {
    this.itemInUrl = getUrlParamNumberValue(window.location.search, 'foto');
    this.activeItem = this.itemInUrl ? +this.itemInUrl : activeItem;
    this.lockedFrom = lockedFrom;
    this.rootElement = rootElement;
    this.isDesktop = isDesktop;
    this.view = view;
    this.previousBreakPoint = previousBreakPoint;
  }

  static getInstance() {
    if (this.instance) {
      return this.instance;
    }
    this.instance = new GalleryState();
    return this.instance;
  }

  setConfiguration({ lockedFrom, rootElement, isDesktop }: GalleryTypes.MainConfig) {
    this.lockedFrom = lockedFrom;

    if (isDesktop) {
      this.isDesktop = isDesktop;
    }

    if (rootElement) {
      this.rootElement = rootElement;
    }

    this.setInitView();
    this.configureListeners();
  }

  setInitView(): void {
    const CLASS_HIDDEN = 'hidden';
    const slideAndScroll = document.querySelector('[data-slide-or-scroll]');
    const grid = document.querySelector('[data-grid]');
    if (this.getView() === 'slideOrScroll') slideAndScroll?.classList.remove(CLASS_HIDDEN);
    else if (this.getView() === 'grid') grid?.classList.remove(CLASS_HIDDEN);
  }

  getConfiguration(): Omit<GalleryTypes.MainConfig, 'isPremiumUser'> {
    return {
      lockedFrom: this.lockedFrom,
      rootElement: this.rootElement,
      isDesktop: this.isDesktop,
      view: this.view,
    };
  }

  // Obal galerie
  getRootElement(): HTMLElement {
    if (!this.rootElement) {
      throw new Error('Gallery: root element not found');
    }
    return this.rootElement;
  }

  // Aktivní položka
  increaseActiveItem() {
    this.activeItem += 1;
  }

  decreaseActiveItem() {
    this.activeItem = this.activeItem > 1 ? this.activeItem - 1 : 1;
  }

  setActiveItem(index: number): void {
    this.activeItem = index > 0 ? index : 1;

    // Aktualizovat v UI
    const label: HTMLElement | null = document.querySelector('[data-counter-actual]');
    if (label) label.innerHTML = this.getActiveItem().toString();

    // Aktualizovat v URL
    updateParamInUrl('foto', this.getActiveItem().toString());
  }

  getActiveItem(): number {
    return this.activeItem;
  }

  setIsDesktop(isDesktop: boolean): void {
    const glide = initializeGlide();
    if (glide) {
      if (isDesktop) {
        glide.enable();
      } else {
        glide.update({ startAt: 0, perView: 1, gap: 0, peek: 0 }).disable();
      }
      this.isDesktop = isDesktop;
    }
  }

  setView(view: GalleryTypes.View): void {
    this.view = view;
  }

  getIsMobile(): boolean {
    return !this.isDesktop;
  }

  getIsGrid(): boolean {
    return this.view === 'grid';
  }

  getIsSlideOrScroll(): boolean {
    return this.view === 'slideOrScroll';
  }

  getView(): GalleryTypes.View {
    return this.view;
  }

  getIsDesktop(): boolean {
    return this.isDesktop;
  }

  setPreviousBreakpoint(breakpoint: GalleryTypes.Breakpoints): void {
    this.previousBreakPoint = breakpoint;
  }

  getPreviousBreakPoint(): GalleryTypes.Breakpoints {
    return this.previousBreakPoint;
  }

  toggleView(): void {
    // Přepnout hodnotu view
    this.view = this.view === 'slideOrScroll' ? 'grid' : 'slideOrScroll';

    const grid: HTMLDivElement | null = document.querySelector('[data-grid]');
    if (!grid) throw new Error('Gallery: grid not found');

    const CLASS_HIDDEN = 'hidden';
    const slideAndScroll = document.querySelector('[data-slide-or-scroll]');
    if (!slideAndScroll) throw new Error('Gallery: slideAndScroll not found');

    switch (this.view) {
      case 'slideOrScroll':
        grid?.classList.add(CLASS_HIDDEN);
        slideAndScroll?.classList.remove(CLASS_HIDDEN);

        break;
      case 'grid':
        GTMTrack('gallery_preview_open');
        this.activeItem = 1;
        grid?.classList.remove(CLASS_HIDDEN);
        slideAndScroll?.classList.add(CLASS_HIDDEN);
        break;
    }

    this.configureListeners();

    // Na gridu zobrazit první položku
    if (this.getIsGrid()) {
      this.setActiveItem(1);
      scrollToTop();
    }
  }

  // Nastavit listenery dle view
  configureListeners(): void {
    const grid: HTMLDivElement | null = document.querySelector('[data-grid]');
    if (!grid) throw new Error('Gallery: grid not found');

    const gridItems: NodeListOf<HTMLDivElement> = grid.querySelectorAll(
      '[data-gallery-item-position-grid]'
    );
    if (gridItems.length === 0) throw new Error('Gallery: grid items not found');

    const items: NodeListOf<HTMLDivElement> = document.querySelectorAll('[data-gallery-item-zoom]');
    if (items.length === 0) throw new Error('Gallery: items zoom not found');

    const arrowButtons: NodeListOf<HTMLButtonElement> =
      document.querySelectorAll('[data-glide-dir]');
    if (arrowButtons.length === 0) throw new Error('Gallery: desktop arrow buttons not found');

    // Nejdřív odstraníme všechny keyboard listenery, abychom zabránili duplikaci
    document.removeEventListener('keydown', handleKeyboardArrows);

    // Na desktopu ve slideru přidat listenery pro 'přechod vpravo/vlevo'
    if (this.getIsDesktop() && this.getIsSlideOrScroll()) {
      // Přidat listener pro klávesové šipky - stejný přístup jako u kliknutí na šipky
      document.addEventListener('keydown', handleKeyboardArrows);

      arrowButtons.forEach((arrowButton) => {
        arrowButton.addEventListener('click', handleCarouselArrows);
      });
    } else {
      // Odebrat listenery při přepnutí režimu
      document.removeEventListener('keydown', handleKeyboardArrows);
      
      arrowButtons.forEach((arrowButton) => {
        arrowButton.removeEventListener('click', handleCarouselArrows);
      });
    }

    // Pouze na desktopu přidat listener pro 'zoom obrázku'
    switch (this.getIsDesktop()) {
      case true:
        items.forEach((item) => {
          item.addEventListener('click', handleZoom);
        });
        break;

      case false:
        items.forEach((item) => {
          item.removeEventListener('click', handleZoom);
        });

        break;
    }

    // Podle view 'slideOrScroll' nebo 'grid' přidat/odebrat listenery
    switch (this.view) {
      case 'slideOrScroll':
        // Odebrat listener pro 'přechod z gridu'
        gridItems.forEach((gridItem: HTMLElement) => {
          gridItem.removeEventListener('click', handleToggleViewTransitions);
        });

        break;
      case 'grid':
        // Přidat listener pro 'přechod z gridu'
        gridItems.forEach((gridItem: HTMLElement) => {
          gridItem.addEventListener('click', handleToggleViewTransitions);
        });

        break;
    }
  }
}

export const galleryState = GalleryState.getInstance();
